// Lib
import { useDispatch } from 'react-redux';
import { List } from 'immutable';

// Utils
import { getLabels } from '../../../../../../../common/elements/utils/elementPropertyUtils';

// Actions
import { addLabelToElements, removeLabelFromElements } from '../../../../../../element/labels/elementLabelActions';
import { createAndAddLabelToElementsThunk } from '../labelThunks';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { Label } from '../../../../../../../common/labels/labelTypes';
import { LabelsToolContentProps } from '../labelComponentPropTypes';

type ReturnType = {
    handleToggleLabelSelection: (label: Label) => void;
    handleCreateLabel: () => void;
    handleSearchSubmit: () => void;
};

const useLabelHandlers = (
    props: LabelsToolContentProps,
    searchString: string,
    setSearchString: (searchString: string) => void,
    elements: List<ImMNElement>,
    allLabels: Label[],
): ReturnType => {
    const { currentUserId } = props;

    const dispatch = useDispatch();

    const hasLabelOnAllElements = (labelId: string) =>
        elements.every((element) => getLabels(element)?.includes(labelId));

    const getLabelApplicationAnalytics = () => ({ usedSearch: !!searchString });

    const handleAddLabelToElements = (label: Label) => {
        dispatch(addLabelToElements(elements, label.id, undefined, getLabelApplicationAnalytics()));
    };

    const handleToggleLabelSelection = (label: Label) => {
        if (hasLabelOnAllElements(label.id)) {
            dispatch(removeLabelFromElements(elements, label.id));
        } else {
            handleAddLabelToElements(label);
        }
    };

    const handleCreateLabel = () => {
        if (!searchString) return;

        const labelData = { name: searchString, creator: currentUserId };
        dispatch(createAndAddLabelToElementsThunk(labelData, elements, getLabelApplicationAnalytics()));
        setSearchString('');
    };

    const handleSearchSubmit = () => {
        const existingLabel = allLabels.find((label) => label.name.toLowerCase() === searchString.toLowerCase());

        if (!existingLabel) {
            handleCreateLabel();
            return;
        }

        if (!hasLabelOnAllElements(existingLabel.id)) handleAddLabelToElements(existingLabel);
    };

    return {
        handleToggleLabelSelection,
        handleCreateLabel,
        handleSearchSubmit,
    };
};

export default useLabelHandlers;
